import React, { useCallback, useEffect, useRef } from "react"
import { MdClose } from "react-icons/md"
import styled from "styled-components"

import {
	MobergButton,
	MobergButtonShape,
	MobergButtonVariant,
} from "../../../../../Components/MobergButton/MobergButton"
import { MobergTheme } from "../../../../../Components/MobergThemes/MobergColors"
import { MobergIconSize } from "../../../../../Components/MobergIcon/MobergIcon"
import { MobergRow, MobergColumn } from "../../../../../Moberg"
import {
	EEGCalibrationStatus,
	EEGCalibrationRecord,
	MonitorPreset,
} from "../../../../../Pages/Data/Visualize/DataReview/Calibration/types"
import { DEFAULT_PX_PER_MM } from "../../../../../Pages/Data/Visualize/DataReview/Calibration/constants"

import {
	ModalContainer,
	ModalHeader,
	ModalTitle,
	ModalContent,
} from "./modal-primitives"
import { useConfirmation, useCalibrationState, useArrowKeys } from "./hooks"
import { CalibrationStatusBanner, CalibrationStatusDetails } from "./CalibrationStatusBanner"
import { MonitorPresetSelector } from "./MonitorPresetSelector"
import { CalibrationControls } from "./CalibrationControls"
import { CalibrationRuler } from "./CalibrationRuler"
import { ResizableCreditCard } from "./ResizableCreditCard"

interface CalibrationModalProps {
	details: CalibrationStatusDetails
	status: EEGCalibrationStatus
	record: EEGCalibrationRecord | undefined
	save: (pxPerMm: number) => void
	clear: () => void
}

export const CalibrationModal: React.FC<CalibrationModalProps> = ({
	details,
	status,
	record,
	save,
	clear,
}) => {
	const cardRef = useRef<HTMLDivElement>(null)
	const modalRef = useRef<HTMLDivElement>(null)

	const savedPxPerMm = record?.pxPerMm ?? DEFAULT_PX_PER_MM
	const calibration = useCalibrationState(savedPxPerMm)
	const { confirm, close } = useConfirmation()
	const onKeyDownCapture = useArrowKeys({ modalRef, onAdjust: calibration.adjustByMm })

	const handleApplyPreset = useCallback(
		(preset: MonitorPreset) => {
			calibration.applyPreset(preset)
			requestAnimationFrame(() => cardRef.current?.focus())
		},
		[calibration.applyPreset],
	)

	const handleManualSelect = useCallback(() => {
		calibration.setManualMode()
		requestAnimationFrame(() => cardRef.current?.focus())
	}, [])

	const handleSave = useCallback(() => {
		confirm(
			"Save calibration?",
			"Do you want to save the current calibration settings?",
			"Save calibration",
			MobergTheme.BLUE,
			() => save(calibration.pxPerMm),
		)
	}, [confirm, save, calibration.pxPerMm])

	const handleClear = useCallback(() => {
		confirm(
			"Clear saved calibration?",
			"Do you want to clear the saved calibration?",
			"Clear calibration",
			MobergTheme.RED,
			() => {
				clear()
				calibration.reset(DEFAULT_PX_PER_MM)
			},
		)
	}, [confirm, clear, calibration.reset])

	const handleRequestClose = useCallback(() => {
		if (!calibration.hasChanges) {
			close()
			return
		}

		confirm(
			"Exit without saving?",
			"Do you want to exit without saving changes?",
			"Exit without saving",
			MobergTheme.RED,
		)
	}, [confirm, close, calibration.hasChanges])

	// focus on mount
	useEffect(() => {
		requestAnimationFrame(() => cardRef.current?.focus())
	}, [])

	return (
		<ModalContainer
			ref={modalRef}
			role="dialog"
			aria-modal="true"
			$variant="calibration"
			onKeyDownCapture={onKeyDownCapture}
		>
			<ModalHeader>
				<ModalTitle>Calibrate display for EEG review</ModalTitle>
				<MobergButton
					theme={MobergTheme.BLACK}
					variant={MobergButtonVariant.CLEAR}
					shape={MobergButtonShape.SQUARE}
					onClick={handleRequestClose}
				>
					<MdClose size={MobergIconSize.REGULAR} />
				</MobergButton>
			</ModalHeader>

			<ModalContent $isCalibration={true}>
				<CalibrationStatusBanner details={details} />
				<TwoColumnLayout>
					<MobergColumn gap={24} style={{ overflow: "hidden" }}>
						<MonitorPresetSelector
							activePreset={calibration.activePreset}
							applyPreset={handleApplyPreset}
							onSelectManual={handleManualSelect}
						/>
					</MobergColumn>

					<RightColumn>
						<div>
							Select a preset on the left or manually adjust the size of the
							references below
						</div>
						<CalibrationControls
							showRevertToSaved={status === EEGCalibrationStatus.VALID}
							adjustByMm={calibration.adjustByMm}
							resetToDefault={() => calibration.reset(DEFAULT_PX_PER_MM)}
							resetToSaved={() => calibration.reset(savedPxPerMm)}
						/>

						<CalibrationRuler pxPerMm={calibration.pxPerMm} />

						<MobergRow
							horizontalAlign="flex-start"
							style={{ padding: "8px 0 16px" }}
						>
							<ResizableCreditCard
								cardRef={cardRef}
								cardWidthPx={calibration.cardWidthPx}
								cardHeightPx={calibration.cardHeightPx}
							/>
						</MobergRow>
					</RightColumn>
				</TwoColumnLayout>
			</ModalContent>

			<MobergRow
				verticalAlign="center"
				horizontalAlign="space-between"
				style={{ padding: "20px 24px 24px", borderTop: "1px solid var(--color-border-light)", gap: 16 }}
			>
				<MobergRow horizontalAlign="flex-start" style={{ gap: 12 }}>
					<MobergButton
						onClick={handleClear}
						theme={MobergTheme.RED}
						variant={MobergButtonVariant.OUTLINE}
						style={{ padding: "10px 24px" }}
					>
						Clear saved calibration
					</MobergButton>
				</MobergRow>
				<MobergRow horizontalAlign="flex-end" style={{ gap: 12 }}>
					<MobergButton
						onClick={handleSave}
						disabled={!calibration.hasChanges}
						theme={MobergTheme.BLUE}
						variant={MobergButtonVariant.FILLED}
						shape={MobergButtonShape.WIDE}
						style={{ padding: "12px 28px" }}
					>
						Save calibration
					</MobergButton>
				</MobergRow>
			</MobergRow>
		</ModalContainer>
	)
}

const TwoColumnLayout = styled.div`
	width: 100%;
	display: grid;
	grid-template-columns: 0.4fr 3fr;
	gap: 32px;
	flex: 1 1 auto;
	min-height: 0;
	align-items: stretch;
`

const RightColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 20px;
	align-items: flex-start;
	color: var(--color-text-primary);
	border-left: 1px solid var(--color-border-light);
	padding-left: 32px;
	overflow-y: auto;
	flex: 1;
	min-height: 0;
	scrollbar-width: thin;
	scrollbar-color: var(--color-scrollbar-thumb) transparent;

	&::-webkit-scrollbar {
		width: 8px;
	}

	&::-webkit-scrollbar-track {
		background: transparent;
	}

	&::-webkit-scrollbar-thumb {
		background-color: var(--color-scrollbar-thumb);
		border-radius: 999px;
		border: 2px solid transparent;
	}

	&::-webkit-scrollbar-thumb:hover {
		opacity: 0.8;
	}
`
